/**
 * hreflang alternates for the 30 supported interface languages.
 *
 * Shared by BOTH rendering paths so they cannot disagree:
 *   - prerender.mjs  bakes the tags into every static page
 *   - useSEO.ts      re-applies them on client-side navigation
 *
 * ── URL scheme ───────────────────────────────────────────────────────────────
 *
 * Path prefixes, not query parameters: `/es/about`, not `/about?lang=es`.
 *
 * The `?lang=` form was replaced because a static host serves one file per
 * path — `/about` and `/about?lang=es` were byte-identical English HTML, and
 * the Spanish title only appeared after hydration. Prefixed paths get their own
 * pre-rendered file, so the translated <title> and description are in the HTML
 * a crawler is served.
 *
 * ── Why the canonical matters ────────────────────────────────────────────────
 *
 * Google ignores an hreflang cluster whose members are not self-canonical. Each
 * language URL must canonicalise to itself; if `/es/about` claimed
 * `<link rel="canonical" href="https://thenamewheel.com/about">` the Spanish
 * page would read as a duplicate of the English one and the whole annotation
 * would collapse. See canonicalFor().
 */

import {
  SUPPORTED_LANGS, DEFAULT_LANG, isSupportedLang, pathWithLang, stripLangPrefix,
} from './languages';

export { SUPPORTED_LANGS, DEFAULT_LANG, isSupportedLang };

export interface Alternate {
  hreflang: string;
  href: string;
}

/**
 * Normalise to the preferred trailing-slash form.
 *
 * The root path `/` already carries a slash and must not become `//`.
 * Every other path gets exactly one trailing slash, regardless of whether one
 * was already present — so callers don't need to know which form they hold.
 */
function withSlash(p: string): string {
  return p === '/' ? '/' : p.endsWith('/') ? p : p + '/';
}

/** Absolute URL for `route` in `lang`, always in the preferred trailing-slash form. */
export function urlForLang(baseUrl: string, route: string, lang: string): string {
  return `${baseUrl}${withSlash(pathWithLang(route, lang))}`;
}

/**
 * The canonical URL for a page. `pathname` may already carry a prefix, so this
 * is safe to call with a live location — it always resolves to the URL the page
 * is actually being served from, in the preferred trailing-slash form.
 */
export function canonicalFor(baseUrl: string, pathname: string): string {
  return `${baseUrl}${withSlash(pathname.replace(/\/$/, '') || '/')}`;
}

/**
 * The full alternate set for one page: 30 languages plus x-default.
 *
 * `route` is the language-free path (`/about`), so the same block is emitted on
 * every language variant of that page. That identity is what makes the
 * annotations reciprocal — each URL lists every URL in its cluster, itself
 * included, which is what Google requires.
 *
 * x-default points at the bare English path: with no prefix, the client falls
 * through to the stored preference and then to navigator.language, so it is the
 * right target for a visitor whose language is not one of the 30.
 */
export function buildAlternates(
  baseUrl: string,
  routeOrPath: string,
  /**
   * Languages to advertise. Defaults to every supported language, which is what
   * the annotation meant when all 30 were treated as equally real.
   *
   * Callers that know which languages are genuinely translated pass that subset
   * instead — see INDEXABLE_LANGS in src/data/seoLocales.ts. Claiming an
   * alternate for a page that is 96% English does not win that language, it
   * costs the cluster: Google verifies alternates, and one it cannot confirm as
   * a real translation is grounds for discarding the annotation entirely.
   */
  langs: readonly string[] = SUPPORTED_LANGS,
): Alternate[] {
  const route = stripLangPrefix(routeOrPath);
  const alts = langs.map(lang => {
    // Map internal 'zh' code to Google's required 'zh-Hans' (Simplified Chinese)
    // for the hreflang attribute, without changing the URL path.
    const hreflangCode = lang === 'zh' ? 'zh-Hans' : lang;
    return { hreflang: hreflangCode, href: urlForLang(baseUrl, route, lang) };
  });
  // x-default only makes sense alongside a choice. With English alone the
  // cluster is a single self-referential tag, which says nothing a canonical
  // does not already say — so emit nothing rather than noise.
  if (alts.length < 2) return [];
  // x-default must also use the preferred trailing-slash form so all members
  // of the cluster are consistent.
  return [...alts, { hreflang: 'x-default', href: `${baseUrl}${withSlash(route)}` }];
}

/** Marks the tags this module owns, so re-renders replace rather than duplicate. */
export const HREFLANG_ATTR = 'data-hreflang-managed';
